import { FC, useMemo } from 'react'

import { Avatar, AvatarGroup, AvatarGroupProps } from '@mui/material'

import { Game } from '../../features/game'
import './GameGroup.scss'

/**
 * Props for the GameGroup component.
 * Extends MUI's AvatarGroupProps to inherit all standard avatar group functionality.
 */
type GameGroupProps = AvatarGroupProps & {
  /** Array of game objects to display as avatars */
  games: Game[]
  /** Whether to shuffle the order of games before displaying. Defaults to true for variety. */
  shuffle?: boolean
}

/**
 * GameGroup Component
 *
 * A specialized avatar group component that displays a collection of games as rounded avatars.
 * Each game is represented by its icon, and the component supports shuffling for visual variety.
 *
 * Features:
 * - Displays up to 4 games by default (configurable via max prop)
 * - Rounded avatar style with overlapping effect (negative spacing)
 * - Optional shuffling of game order
 * - Fallback handling for different icon data types
 * - Tooltip support via title attribute
 *
 * @param games - Array of Game objects to display
 * @param shuffle - Whether to randomize the order of games (default: true)
 * @param avatarGroupProps - Additional props passed to MUI AvatarGroup component
 *
 * @example
 * ```tsx
 * <GameGroup
 *   games={gameList}
 *   shuffle={false}
 *   max={6}
 *   spacing={-8}
 * />
 * ```
 */
export const GameGroup: FC<GameGroupProps> = ({ games, shuffle = true, ...avatarGroupProps }) => {
  // Create a shuffled copy of games array if shuffle is enabled
  // This provides visual variety when the same games are displayed multiple times
  const shuffledGames = useMemo(() => {
    return shuffle ? [...games].sort(() => Math.random() - 0.5) : games
  }, [games, shuffle])

  return (
    <AvatarGroup max={4} variant="rounded" className="GameGroup" spacing={-5} {...avatarGroupProps}>
      {shuffledGames.map((game) => {
        // Handle different icon data types - Game.icon can be either a string URL
        // or a FileUploadResponse object. We extract the string URL when needed.
        const src = typeof game.icon === 'string' ? game.icon : ''
        return <Avatar className="GameGroup__avatar" key={game.id} src={src} alt={game.name || ''} title={game.name || ''} />
      })}
    </AvatarGroup>
  )
}
